import type { ReactNode } from "react";
import { Text } from "../../../../atoms/Text";
import { cn } from "../../../../lib/cn";
import { hideBelowWidthClass } from "../collapse";
import { Chevron } from "../primitives";
import type { LedgerCollapse } from "../types";

export interface BandProps {
  collapse: LedgerCollapse;
  primary: string;
  /** Optional visual before the label, such as a status dot. */
  lead?: ReactNode;
  /** Rendered after a "·" — a gang, a plot, a date range. */
  secondary?: string;
  count?: number;
  /** Generic slot beside the label; the ledger screens put a percentage here. */
  inline?: ReactNode;
  /** Hide the inline slot at or below this width. Always shown when omitted. */
  inlineHiddenBelow?: number;
  /** Sits in a VALUE_WIDTH box so band totals land on the Value column. */
  trailing?: ReactNode;
  open: boolean;
  onToggle: () => void;
  /**
   * Primary action on the label, beside the toggle: opening the thing the
   * band groups by (a plot, a site, a labour item). Without it the label is a
   * second way to toggle, which is what a band with nothing to open wants.
   */
  onLabelClick?: () => void;
  showValues?: boolean;
  /** Reserve the same trailing slot used by row actions. */
  showActions?: boolean;
  /** ID of the collapsible region controlled by this band. */
  panelId?: string;
  "data-testid"?: string;
}

const labelButtonClass =
  "group/band -mx-1 flex min-w-0 cursor-pointer items-center gap-2 rounded-sm border-0 bg-transparent px-1 text-left " +
  "focus-visible:outline-2 focus-visible:outline-border-focused";

type BandLabelProps = Pick<
  BandProps,
  | "primary"
  | "lead"
  | "secondary"
  | "count"
  | "open"
  | "onToggle"
  | "onLabelClick"
  | "panelId"
>;

function BandLabel(props: BandLabelProps) {
  const labelToggles = props.onLabelClick === undefined;
  return (
    <>
      <button
        type="button"
        onClick={props.onToggle}
        aria-expanded={props.open}
        aria-controls={props.panelId}
        aria-label={`${props.open ? "Collapse" : "Expand"} ${props.primary}`}
        className="shrink-0 cursor-pointer rounded-full border-0 bg-transparent p-0.5 text-inherit focus-visible:outline-2 focus-visible:outline-border-focused"
      >
        <Chevron open={props.open} />
      </button>
      <button
        type="button"
        onClick={props.onLabelClick ?? props.onToggle}
        aria-expanded={labelToggles ? props.open : undefined}
        aria-controls={labelToggles ? props.panelId : undefined}
        className={labelButtonClass}
      >
        {props.lead}
        <span className="min-w-0 truncate font-semibold text-(length:--ledger-text-title) leading-[1.43] transition-colors duration-150 group-hover/band:text-content-accent">
          {props.primary}
        </span>
        {props.secondary ? (
          <span className="min-w-0 truncate text-(length:--ledger-text-title) leading-[1.43] text-content-secondary">
            · {props.secondary}
          </span>
        ) : null}
      </button>
      {props.count !== undefined ? (
        <Text
          variant="inherit"
          color="secondary"
          className="text-(length:--ledger-text-cell) leading-[1.66]"
        >
          {props.count}
        </Text>
      ) : null}
    </>
  );
}

function BandTail(props: BandProps) {
  return (
    <>
      {props.inline ? (
        <div
          className={cn(
            "ml-2 flex items-center gap-1.5",
            props.inlineHiddenBelow
              ? hideBelowWidthClass(
                  props.collapse.container,
                  props.inlineHiddenBelow,
                )
              : undefined,
          )}
        >
          {props.inline}
        </div>
      ) : null}
      <div className="flex-1" />
      {props.showValues !== false ? (
        <div className="w-(--ledger-value-width) shrink-0 text-right">
          {props.trailing}
        </div>
      ) : null}
      {props.showActions ? (
        <div aria-hidden="true" className="w-9 shrink-0" />
      ) : null}
    </>
  );
}

/**
 * A collapsible group heading. Knows about labels, a count, one generic inline
 * slot and one trailing slot — nothing about what is inside the group.
 *
 * The toggle and the label are sibling controls rather than one nested inside
 * the other: a button inside a button is invalid, and it costs both keyboard
 * and screen-reader users the second action.
 */
export const Band = (props: BandProps) => {
  return (
    <div
      data-testid={props["data-testid"]}
      className="flex min-h-(--ledger-band-min-height) w-full items-center gap-2 border-b border-border-default bg-surface-raised py-1.5 pl-2 pr-4 text-left"
    >
      <BandLabel {...props} />
      <BandTail {...props} />
    </div>
  );
};
